import * as XLSX from "xlsx";

/**
 * Leitura de planilha de cadastro (CSV ou Excel) no navegador, comum às
 * importações de fornecedores, clientes e usuários.
 *
 * A planilha é lida aqui, com a mesma biblioteca que já gera o Excel dos
 * relatórios, e o backend recebe as linhas já mapeadas por campo. Assim o
 * usuário vê a prévia (o que entra, o que já existe, o que está errado) antes
 * de confirmar, e o backend não precisa de nada novo para abrir arquivo.
 *
 * Cada importação descreve suas colunas como
 * `{ campo, rotulo, obrigatorio?, apelidos: [] }`. O cabeçalho da planilha é
 * normalizado (sem acento, sem espaço, minúsculo) e comparado com os apelidos:
 * "CPF/CNPJ", "cpf cnpj" e "CNPJ" caem todos em `cpfCnpj`.
 */

export function normalizarCabecalho(texto) {
	return String(texto ?? "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
}

function campoDoCabecalho(cabecalho, colunas) {
	const chave = normalizarCabecalho(cabecalho);
	if (!chave) return null;
	const coluna = colunas.find((c) => c.apelidos.includes(chave));
	return coluna ? coluna.campo : null;
}

/** Valor da célula como texto, sem perder dígitos de número grande (CNPJ, telefone). */
function celulaComoTexto(valor) {
	if (valor === null || valor === undefined) return "";
	if (typeof valor === "number") {
		// Inteiro grande: String() preserva todos os dígitos até 2^53 — o `w`
		// formatado do Excel viraria "1,23457E+13" num CNPJ.
		return Number.isInteger(valor) ? String(valor) : String(valor).replace(".", ",");
	}
	if (valor instanceof Date) {
		// Célula de data do Excel: vira dd/mm/aaaa, que é o que o backend lê.
		const d = String(valor.getDate()).padStart(2, "0");
		const m = String(valor.getMonth() + 1).padStart(2, "0");
		return `${d}/${m}/${valor.getFullYear()}`;
	}
	return String(valor).trim();
}

/**
 * CSV salvo pelo Excel em português costuma vir em Windows-1252, não UTF-8.
 * Tenta UTF-8 estrito primeiro; se houver byte inválido, cai para 1252.
 */
function decodificarCsv(buffer) {
	try {
		return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
	} catch (e) {
		return new TextDecoder("windows-1252").decode(buffer);
	}
}

/**
 * Encontra a linha de cabeçalho: a que mais colunas conhecidas tem, entre as
 * 10 primeiras. Planilha com título em cima da tabela continua funcionando.
 */
function localizarCabecalho(matriz, colunas) {
	let melhor = { indice: -1, mapa: [], reconhecidas: 0 };
	const limite = Math.min(matriz.length, 10);
	for (let i = 0; i < limite; i++) {
		const linha = matriz[i] || [];
		const mapa = linha.map((celula) => campoDoCabecalho(celula, colunas));
		const reconhecidas = mapa.filter(Boolean).length;
		if (reconhecidas > melhor.reconhecidas) {
			melhor = { indice: i, mapa, reconhecidas, cabecalhos: linha.map(celulaComoTexto) };
		}
	}
	return melhor;
}

/**
 * Lê o arquivo e devolve as linhas mapeadas por campo.
 *
 * @param {File} file
 * @param {object[]} colunas colunas aceitas pela importação
 * @param {{ exemploCabecalho: string, entidade: string }} textos para as mensagens de erro
 * @returns {Promise<{ linhas: object[], colunasReconhecidas: {cabecalho:string,campo:string}[], colunasIgnoradas: string[] }>}
 */
export async function lerPlanilha(file, colunas, { exemploCabecalho, entidade }) {
	const buffer = await file.arrayBuffer();
	const ehCsv = /\.csv$/i.test(file.name) || file.type === "text/csv";

	let workbook;
	if (ehCsv) {
		// raw: mantém tudo como texto. Sem isso o CSV "01234567890" vira número
		// e perde o zero à esquerda antes de chegar ao backend.
		workbook = XLSX.read(decodificarCsv(buffer), { type: "string", raw: true });
	} else {
		// cellDates: célula de data vem como Date, não como número serial.
		workbook = XLSX.read(buffer, { type: "array", cellDates: true });
	}

	const primeiraAba = workbook.SheetNames[0];
	if (!primeiraAba) throw new Error("A planilha está vazia.");

	const matriz = XLSX.utils.sheet_to_json(workbook.Sheets[primeiraAba], {
		header: 1,
		raw: true,
		defval: "",
	});

	const cabecalho = localizarCabecalho(matriz, colunas);
	if (cabecalho.indice < 0) {
		throw new Error(
			`Não encontrei o cabeçalho da planilha. A primeira linha da tabela precisa ter os nomes das colunas (ex.: ${exemploCabecalho}).`
		);
	}

	const obrigatorios = colunas.filter((c) => c.obrigatorio).map((c) => c.campo);
	const faltando = obrigatorios.filter((campo) => !cabecalho.mapa.includes(campo));
	if (faltando.length) {
		const rotulos = faltando.map((campo) => colunas.find((c) => c.campo === campo).rotulo);
		throw new Error(`A planilha precisa ter a(s) coluna(s): ${rotulos.join(", ")}. Baixe o modelo para ver o formato esperado.`);
	}

	const colunasReconhecidas = [];
	const colunasIgnoradas = [];
	cabecalho.mapa.forEach((campo, indice) => {
		const titulo = cabecalho.cabecalhos[indice];
		if (!titulo) return;
		if (campo) colunasReconhecidas.push({ cabecalho: titulo, campo });
		else colunasIgnoradas.push(titulo);
	});

	const linhas = [];
	for (let i = cabecalho.indice + 1; i < matriz.length; i++) {
		const celulas = matriz[i] || [];
		const linha = { linha: i + 1 };
		let temConteudo = false;
		cabecalho.mapa.forEach((campo, indice) => {
			if (!campo) return;
			const valor = celulaComoTexto(celulas[indice]);
			if (valor !== "") temConteudo = true;
			linha[campo] = valor;
		});
		if (temConteudo) linhas.push(linha);
	}

	if (!linhas.length) throw new Error(`A planilha não tem nenhuma linha de ${entidade} abaixo do cabeçalho.`);

	return { linhas, colunasReconhecidas, colunasIgnoradas };
}

/**
 * Gera e baixa o modelo em Excel: uma aba com as colunas e uma linha de
 * exemplo, outra com as instruções de preenchimento.
 */
export function baixarModeloPlanilha({ colunas, exemplo, instrucoes, aba, arquivo }) {
	const cabecalhos = colunas.map((c) => c.rotulo);
	const ws = XLSX.utils.aoa_to_sheet([cabecalhos, exemplo]);
	ws["!cols"] = cabecalhos.map((titulo) => ({ wch: Math.max(16, titulo.length + 2) }));

	const wsInstrucoes = XLSX.utils.aoa_to_sheet(instrucoes);
	wsInstrucoes["!cols"] = [{ wch: 30 }, { wch: 110 }];

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, aba);
	XLSX.utils.book_append_sheet(wb, wsInstrucoes, "Instruções");
	XLSX.writeFile(wb, arquivo);
}
